import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  FaAngleDoubleLeft as LeftIcon,
  FaAngleDoubleRight as RightIcon,
} from 'react-icons/fa';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import type { BoxProps } from '@mui/material';
import {
  Box,
  Drawer,
  Grid,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';

import type { ChatbotTypes } from 'src/types/chatbot';
import Footer from 'src/components/common/Footer';
import Header from 'src/components/app/Header';
import ButtonChatbotModal from 'src/components/chatbot/ChatbotModalButton';
import DownloadManager from 'src/components/downloadManager/DownloadManager';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import FollowUpInspirationsButton from 'src/components/followUpInspirations/components/FollowUpInspirationsButton';

import { styles } from './index.styles';

interface PageFrameContextValue {
  setShowSidebar: Dispatch<SetStateAction<boolean>>;
}

const PageFrameContext = createContext<PageFrameContextValue>({
  setShowSidebar: () => {},
});

export const usePageFrame = () => useContext(PageFrameContext);

export interface PageFrameProps extends BoxProps {
  actionBanner?: ReactNode;
  header?: ReactNode | false;
  infoBanner?: ReactNode | string;
  sidebar?: ReactNode;
  pageFrameWidth?: number;
  chatRoomId?: string;
  chatbotType?: ChatbotTypes;
  guidedTourButton?: ReactNode;
  showFollowUpInspirationsButton?: boolean;
}

const PageFrame: FC<PageFrameProps> = ({
  actionBanner,
  children,
  className,
  guidedTourButton = null,
  header,
  infoBanner,
  sidebar,
  sx = [],
  chatRoomId = '',
  chatbotType,
  pageFrameWidth,
  showFollowUpInspirationsButton,
}) => {
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const [showSidebar, setShowSidebar] = useState(upLg);
  const { showDownloadManager } = useDownloadManager();

  // Reset 'showSidebar' to the viewport specific default when the breakpoint was crossed.
  useEffect(() => {
    // reset the sidebar at the end of the execution queue to prevent an issue where the page can not be interacted with
    setTimeout(() => setShowSidebar(upLg), 0);
  }, [upLg]);

  return (
    <PageFrameContext.Provider value={{ setShowSidebar }}>
      <Box
        className={classNames('PageFrame-root', className)}
        sx={[styles.pageFrame, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {header ? (
          <Box className="PageFrame-header">{header}</Box>
        ) : (
          <Header
            onRightIconClick={
              sidebar ? () => setShowSidebar(!showSidebar) : undefined
            }
            guidedTourButton={guidedTourButton}
          />
        )}

        <Grid
          className="PageFrame-contentContainer"
          container
          sx={[
            styles.contentContainer,
            !showSidebar && upLg && styles.collapsed,
          ]}
        >
          {!!sidebar && !upLg && (
            <Drawer
              anchor="right"
              className="PageFrame-sidebarDrawer"
              ModalProps={{ keepMounted: true }}
              onClose={() => setShowSidebar(false)}
              open={showSidebar}
              sx={styles.sidebarDrawer}
            >
              <Box
                sx={{ mt: -2, mr: -2, display: 'flex', justifyContent: 'end' }}
              >
                <IconButton onClick={() => setShowSidebar(false)}>
                  <CloseRoundedIcon fontSize="large" />
                </IconButton>
              </Box>

              {sidebar}
            </Drawer>
          )}

          {!!sidebar && upLg && (
            <Grid item className="PageFrame-sidebar" sx={styles.sidebar} xs={3}>
              <IconButton
                data-test-id="collapse-button"
                onClick={() => setShowSidebar(!showSidebar)}
                size="small"
                sx={[styles.collapseButton]}
              >
                {showSidebar ? <LeftIcon /> : <RightIcon />}
              </IconButton>

              <Box
                className="PageFrame-sidebarContentContainer"
                sx={styles.sidebarContentContainer}
              >
                {sidebar}
              </Box>

              <Footer />
            </Grid>
          )}

          <Grid
            className="PageFrame-main"
            item
            sx={styles.main}
            xs={!!sidebar && upLg ? 9 : 12}
          >
            {showFollowUpInspirationsButton && <FollowUpInspirationsButton />}
            <Box
              sx={styles.children(pageFrameWidth)}
              className="PageFrame-children"
            >
              {!!infoBanner && (
                <Box sx={styles.infoBanner} className="PageFrame-infoBanner">
                  {infoBanner}
                </Box>
              )}
              {children}

              {
                // Render the footer within the main scroll container, when there is no sidebar.
                // Or, when in mobile-view but the sidebar is moved to the drawer.
                (!sidebar || (!!sidebar && !upLg)) && <Footer />
              }

              <Stack direction="row" spacing={2} sx={styles.actionBar}>
                {showDownloadManager && (
                  <Stack direction="column" sx={styles.downloadManager}>
                    <DownloadManager />
                  </Stack>
                )}
                {chatbotType && chatRoomId && (
                  <Stack direction="column" sx={styles.chatBotWrapper}>
                    <ButtonChatbotModal
                      chatRoomId={chatRoomId}
                      type={chatbotType}
                    />
                  </Stack>
                )}
              </Stack>
            </Box>
            {!!actionBanner && (
              <Box className="PageFrame-actionBanner">{actionBanner}</Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </PageFrameContext.Provider>
  );
};

export default PageFrame;
